import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const cardVariants = cva(
  'min-w-0 break-words overflow-hidden rounded-card [overflow-wrap:anywhere]',
  {
    variants: {
      variant: {
        standard: 'bg-surface-sheet border border-border',
        soft: 'bg-surface-soft border border-border',
        dark: 'bg-surface-inverse border border-border text-text-inverse [&_h3]:text-text-inverse [&_p]:text-text-inverse',
      },
    },
    defaultVariants: {
      variant: 'standard',
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, children, ...props }, ref) => {
    // Only a complete section composition transfers inset ownership to children.
    // Mixed/custom content keeps the root inset, including plain text siblings.
    const sections = React.Children.toArray(children);
    const composed =
      sections.length > 0 &&
      sections.every(
        (child) =>
          React.isValidElement(child) &&
          (child.type === CardHeader || child.type === CardContent || child.type === CardFooter),
      );
    return (
      <div
        ref={ref}
        className={cn(
          cardVariants({ variant }),
          composed ? '[:where(&)]:p-0' : 'p-4 sm:p-5',
          className,
        )}
        data-card-layout={composed ? 'composed' : 'mixed'}
        data-experience-card-variant={variant ?? 'standard'}
        {...props}
      >
        {children}
      </div>
    );
  },
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      data-card-section
      data-card-header
      ref={ref}
      className={cn(
        'flex min-w-0 flex-wrap items-start gap-x-4 gap-y-3 [&>div]:min-w-0 [&>div:first-child]:flex-[1_1_16rem] [&>h3]:flex-[1_1_16rem] [&>p]:basis-full [&>div:not(:first-child)]:ml-auto [&>div:not(:first-child)]:flex [&>div:not(:first-child)]:flex-[0_1_auto] [&>div:not(:first-child)]:flex-wrap [&>div:not(:first-child)]:justify-start [&>div:not(:first-child)]:gap-2 [:where(&:not([data-card-layout=mixed]>*))]:p-5 [:where(&:not([data-card-layout=mixed]>*))]:pb-3',
        className,
      )}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'min-w-0 break-normal font-body text-card-title text-text-heading [overflow-wrap:break-word]',
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('min-w-0 break-words font-body text-body-sm text-text-muted', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      data-card-section
      data-card-content
      ref={ref}
      className={cn(
        'min-w-0 [:where(&:not([data-card-layout=mixed]>*))]:p-5 [:where([data-card-header]+&)]:pt-0',
        className,
      )}
      {...props}
    />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-card-section
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-3 [:where(&:not([data-card-layout=mixed]>*))]:px-5 [:where(&:not([data-card-layout=mixed]>*))]:pb-5',
        className,
      )}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
